import { Suspense, lazy } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { AuthProvider } from '@/contexts/AuthContext'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { Layout } from '@/components/Layout'
// v0.16.0 — Code-splitting: Login giữ eager (màn hình đầu tiên), toàn bộ
// trang còn lại lazy để bundle khởi động nhỏ; trang nặng (Reports ~recharts,
// Payslip ~jspdf/html2canvas, Salary/Logs ~xlsx) chỉ tải khi người dùng mở.
import LoginPage from '@/pages/LoginPage'

const ChangePasswordPage = lazy(() => import('@/pages/ChangePasswordPage'))
const DashboardPage      = lazy(() => import('@/pages/DashboardPage'))
const IncomePage         = lazy(() => import('@/pages/IncomePage'))
const AttendanceMyPage   = lazy(() => import('@/pages/AttendanceMyPage'))
const ProfilePage        = lazy(() => import('@/pages/ProfilePage'))
const ApprovalsPage      = lazy(() => import('@/pages/ApprovalsPage'))
const ChatPage           = lazy(() => import('@/pages/ChatPage'))
const NewsPage           = lazy(() => import('@/pages/NewsPage'))
const LeavePage          = lazy(() => import('@/pages/LeavePage'))
const MiaPaymentPage     = lazy(() => import('@/pages/MiaPaymentPage'))
const OfficeIncomingPage = lazy(() => import('@/pages/OfficeIncomingPage'))
const SecurityPage       = lazy(() => import('@/pages/SecurityPage'))
const GuardShiftPage     = lazy(() => import('@/pages/GuardShiftPage'))
const GuardRosterPage    = lazy(() => import('@/pages/GuardRosterPage'))
const UnauthorizedPage   = lazy(() => import('@/pages/UnauthorizedPage'))
const DepartmentsPage    = lazy(() => import('@/pages/DepartmentsPage'))
const EmployeesPage      = lazy(() => import('@/pages/EmployeesPage'))
const ConfigPage         = lazy(() => import('@/pages/ConfigPage'))
const GradesPage         = lazy(() => import('@/pages/GradesPage'))
const UsersPage          = lazy(() => import('@/pages/UsersPage'))
const AttendancePage     = lazy(() => import('@/pages/AttendancePage'))
const AttendanceDailyPage    = lazy(() => import('@/pages/AttendanceDailyPage'))
const AttendanceSettingsPage = lazy(() => import('@/pages/AttendanceSettingsPage'))
const PermissionsPage    = lazy(() => import('@/pages/PermissionsPage'))
const SalaryPage         = lazy(() => import('@/pages/SalaryPage'))
const PayslipPage        = lazy(() => import('@/pages/PayslipPage'))
const LogsPage           = lazy(() => import('@/pages/LogsPage'))
const PromotionsPage     = lazy(() => import('@/pages/PromotionsPage'))
const BonusPage          = lazy(() => import('@/pages/BonusPage'))
const ReportsPage        = lazy(() => import('@/pages/ReportsPage'))
const TmsCatalogPage     = lazy(() => import('@/pages/TmsCatalogPage'))
const TmsFleetPage       = lazy(() => import('@/pages/TmsFleetPage'))
const TmsOrdersPage      = lazy(() => import('@/pages/TmsOrdersPage'))
const TmsDispatchPage    = lazy(() => import('@/pages/TmsDispatchPage'))
const DriverAppPage      = lazy(() => import('@/pages/DriverAppPage'))

function PageFallback() {
  return (
    <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">
      Đang tải trang…
    </div>
  )
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/unauthorized" element={<UnauthorizedPage />} />
            <Route
              path="/change-password"
              element={
                <ProtectedRoute>
                  <ChangePasswordPage />
                </ProtectedRoute>
              }
            />

            {/* v0.46 — app lái xe: giao diện riêng, không sidebar */}
            <Route path="/lai-xe" element={<ProtectedRoute><DriverAppPage /></ProtectedRoute>} />

            <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              <Route index element={<DashboardPage />} />
              <Route path="income"      element={<IncomePage />} />
              <Route path="chat"        element={<ChatPage />} />
              <Route path="news"        element={<NewsPage />} />
              <Route path="my-attendance" element={<AttendanceMyPage />} />
              <Route path="leave"       element={<LeavePage />} />
              <Route path="guard-shift" element={<GuardShiftPage />} />
              <Route path="guard-roster" element={<GuardRosterPage />} />
              <Route path="mia-payment" element={<MiaPaymentPage />} />
              <Route path="office/incoming" element={<OfficeIncomingPage />} />
              <Route path="profile"     element={<ProfilePage />} />
              <Route path="tms/catalog" element={<TmsCatalogPage />} />
              <Route path="tms/fleet"   element={<TmsFleetPage />} />
              <Route path="tms/orders"  element={<TmsOrdersPage />} />
              <Route path="tms/dispatch" element={<TmsDispatchPage />} />
              <Route path="payslip"     element={<PayslipPage />} />
              <Route path="attendance"  element={<ProtectedRoute roles={['truong_phong','admin_luong','admin_he_thong']}><AttendancePage /></ProtectedRoute>} />
              <Route path="attendance/daily" element={<ProtectedRoute roles={['truong_phong','admin_luong','admin_he_thong']}><AttendanceDailyPage /></ProtectedRoute>} />
              <Route path="attendance/settings" element={<ProtectedRoute roles={['admin_luong','admin_he_thong']}><AttendanceSettingsPage /></ProtectedRoute>} />
              <Route path="salary"      element={<ProtectedRoute roles={['truong_phong','admin_luong','admin_he_thong']}><SalaryPage /></ProtectedRoute>} />
              <Route path="approvals"   element={<ProtectedRoute roles={['admin_luong','admin_he_thong']}><ApprovalsPage /></ProtectedRoute>} />
              <Route path="security"    element={<ProtectedRoute roles={['admin_he_thong']}><SecurityPage /></ProtectedRoute>} />
              <Route path="employees"   element={<ProtectedRoute roles={['admin_luong','admin_he_thong']}><EmployeesPage /></ProtectedRoute>} />
              <Route path="promotions"  element={<ProtectedRoute roles={['admin_luong','admin_he_thong']}><PromotionsPage /></ProtectedRoute>} />
              <Route path="bonus"       element={<ProtectedRoute roles={['admin_luong','admin_he_thong']}><BonusPage /></ProtectedRoute>} />
              <Route path="departments" element={<ProtectedRoute roles={['admin_luong','admin_he_thong']}><DepartmentsPage /></ProtectedRoute>} />
              <Route path="reports"     element={<ProtectedRoute roles={['admin_luong','admin_he_thong']}><ReportsPage /></ProtectedRoute>} />
              <Route path="users"       element={<ProtectedRoute roles={['admin_he_thong']}><UsersPage /></ProtectedRoute>} />
              <Route path="permissions" element={<ProtectedRoute roles={['admin_he_thong']}><PermissionsPage /></ProtectedRoute>} />
              <Route path="logs"        element={<ProtectedRoute roles={['admin_he_thong']}><LogsPage /></ProtectedRoute>} />
              <Route path="config"      element={<ProtectedRoute roles={['admin_he_thong']}><ConfigPage /></ProtectedRoute>} />
              <Route path="grades"      element={<ProtectedRoute roles={['admin_he_thong']}><GradesPage /></ProtectedRoute>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  )
}
